import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Send, Sparkles, Calendar, Route, ListChecks, Award, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function PathsTab({ courses, paths, refreshPaths }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", course_ids: [] });

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", description: "", course_ids: [] });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, description: p.description || "", course_ids: p.course_ids || [] });
    setOpen(true);
  };

  const toggleCourse = (cid) => {
    setForm((p) =>
      p.course_ids.includes(cid)
        ? { ...p, course_ids: p.course_ids.filter((x) => x !== cid) }
        : { ...p, course_ids: [...p.course_ids, cid] }
    );
  };

  const submit = async () => {
    if (!form.title || form.course_ids.length === 0) {
      toast.error("Título y al menos un curso requerido");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/admin/paths/${editing.id}`, form);
        toast.success("Ruta actualizada");
      } else {
        await api.post("/admin/paths", form);
        toast.success("Ruta creada");
      }
      setOpen(false);
      refreshPaths();
    } catch {
      toast.error("Error al guardar la ruta");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar ruta? También se removerán sus asignaciones.")) return;
    await api.delete(`/admin/paths/${id}`);
    toast.success("Ruta eliminada");
    refreshPaths();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-1">Rutas de capacitación</p>
          <h3 className="font-heading text-xl text-[#1A1A1A]">Define recorridos formativos</h3>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="create-path-button">
              <Plus className="h-4 w-4 mr-2" /> Nueva ruta
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Editar ruta" : "Crear ruta"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Nombre de la ruta" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="path-title-input" />
              <Textarea placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">Cursos incluidos (en orden)</p>
                <div className="max-h-60 overflow-auto border border-[#E5E7EB] rounded-md p-2 space-y-1">
                  {courses.map((c) => {
                    const i = form.course_ids.indexOf(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-[#FFFFFF] rounded">
                        <input
                          type="checkbox"
                          checked={i >= 0}
                          onChange={() => toggleCourse(c.id)}
                          className="accent-[#1B2E7A]"
                          data-testid={`path-course-${c.id}`}
                        />
                        <span className="flex-1">{c.title}</span>
                        {i >= 0 && <span className="text-[10px] text-[#6B7280]">#{i + 1}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="save-path-button">
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {paths.length === 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-md p-8 text-center text-[#737373]">
            Aún no hay rutas. Crea la primera para asignarla a varios vendedores a la vez.
          </div>
        )}
        {paths.map((p) => (
          <div key={p.id} className="bg-white border border-[#E5E7EB] rounded-md p-5" data-testid={`path-row-${p.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">Ruta · {p.course_ids?.length || 0} cursos</p>
                <h4 className="font-heading text-lg text-[#1A1A1A]">{p.title}</h4>
                {p.description && <p className="text-sm text-[#374151] mt-1 max-w-2xl">{p.description}</p>}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => openEdit(p)} variant="outline" className="border-[#E5E7EB]">Editar</Button>
                <Button onClick={() => remove(p.id)} variant="outline" className="border-[#E5E7EB] text-[#DC2626]">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {p.course_ids?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {p.course_ids.map((cid, i) => {
                  const c = courses.find((x) => x.id === cid);
                  return (
                    <span key={cid} className="text-xs px-2.5 py-1 rounded-md bg-[#F3F4F6] text-[#374151]">
                      {i + 1}. {c?.title || cid}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AssignmentsTab({ courses, paths, users, assignments, refreshAssignments }) {
  const [form, setForm] = useState({
    item_type: "course",
    item_id: "",
    user_ids: [],
    due_date: "",
    notify: true,
  });
  const [filter, setFilter] = useState("all");

  const submit = async () => {
    if (!form.item_id || form.user_ids.length === 0) {
      toast.error("Selecciona el recurso y al menos un vendedor");
      return;
    }
    try {
      const r = await api.post("/admin/assignments", form);
      toast.success(`Asignación creada · ${r.data.created.length} usuario(s)`);
      setForm({ item_type: "course", item_id: "", user_ids: [], due_date: "", notify: true });
      refreshAssignments();
    } catch {
      toast.error("Error al asignar");
    }
  };

  const remind = async (id) => {
    try {
      const r = await api.post(`/admin/assignments/${id}/remind`);
      if (r.data.status === "mocked") {
        toast.message("Correo simulado (sin API key Resend)", { description: "Configura RESEND_API_KEY para envío real." });
      } else if (r.data.status === "sent") {
        toast.success("Recordatorio enviado");
      } else {
        toast.error("No se pudo enviar");
      }
    } catch {
      toast.error("Error al enviar recordatorio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar asignación?")) return;
    await api.delete(`/admin/assignments/${id}`);
    refreshAssignments();
  };

  const items = form.item_type === "course" ? courses : paths;
  const filtered = filter === "all" ? assignments : assignments.filter((a) => a.status === filter);
  const today = new Date();

  return (
    <div className="space-y-8">
      <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3">Nueva asignación</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#6B7280] block mb-1">Tipo</label>
            <select
              value={form.item_type}
              onChange={(e) => setForm({ ...form, item_type: e.target.value, item_id: "" })}
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
              data-testid="assignment-type-select"
            >
              <option value="course">Curso individual</option>
              <option value="path">Ruta de capacitación</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6B7280] block mb-1">{form.item_type === "course" ? "Curso" : "Ruta"}</label>
            <select
              value={form.item_id}
              onChange={(e) => setForm({ ...form, item_id: e.target.value })}
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
              data-testid="assignment-item-select"
            >
              <option value="">Seleccionar...</option>
              {items.map((x) => (
                <option key={x.id} value={x.id}>{x.title}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-[#6B7280] block mb-1">Vendedores</label>
            <div className="max-h-40 overflow-auto border border-[#E5E7EB] rounded-md p-2 bg-white">
              {users.length === 0 && <p className="text-sm text-[#737373] p-2">Aún no hay vendedores registrados.</p>}
              {users.map((u) => (
                <label key={u.user_id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.user_ids.includes(u.user_id)}
                    onChange={() =>
                      setForm((p) => ({
                        ...p,
                        user_ids: p.user_ids.includes(u.user_id)
                          ? p.user_ids.filter((x) => x !== u.user_id)
                          : [...p.user_ids, u.user_id],
                      }))
                    }
                    className="accent-[#1B2E7A]"
                    data-testid={`assignment-user-${u.user_id}`}
                  />
                  <span className="flex-1">{u.name}</span>
                  <span className="text-xs text-[#6B7280]">{u.email}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6B7280] block mb-1">Fecha límite (opcional)</label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="assignment-due-date" />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              id="notify"
              checked={form.notify}
              onChange={(e) => setForm({ ...form, notify: e.target.checked })}
              className="accent-[#1B2E7A]"
            />
            <label htmlFor="notify" className="text-sm text-[#374151]">Enviar correo al asignar</label>
          </div>
        </div>
        <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white mt-4" data-testid="submit-assignment">
          <Send className="h-4 w-4 mr-2" /> Asignar
        </Button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Asignaciones</p>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-[#E5E7EB] rounded-md px-3 py-1.5 text-xs bg-white"
            data-testid="assignment-filter"
          >
            <option value="all">Todas</option>
            <option value="pending">Pendientes</option>
            <option value="completed">Completadas</option>
          </select>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FFFFFF] text-[#737373]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Vendedor</th>
                <th className="text-left px-5 py-3 font-medium">Recurso</th>
                <th className="text-left px-5 py-3 font-medium">Tipo</th>
                <th className="text-left px-5 py-3 font-medium">Fecha límite</th>
                <th className="text-left px-5 py-3 font-medium">Estado</th>
                <th className="text-right px-5 py-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#737373]">Sin asignaciones.</td>
                </tr>
              )}
              {filtered.map((a) => {
                let dueState = null;
                if (a.due_date && a.status === "pending") {
                  const d = new Date(a.due_date);
                  const days = Math.ceil((d - today) / 86400000);
                  if (days < 0) dueState = { label: "Vencido", color: "text-[#DC2626]" };
                  else if (days <= 3) dueState = { label: `${days}d restantes`, color: "text-[#EAB308]" };
                  else dueState = { label: `${days}d restantes`, color: "text-[#737373]" };
                }
                return (
                  <tr key={a.id} className="border-t border-[#E5E7EB]" data-testid={`assignment-row-${a.id}`}>
                    <td className="px-5 py-3 text-[#1A1A1A] font-medium">{a.user_name}</td>
                    <td className="px-5 py-3 text-[#374151]">{a.item_title}</td>
                    <td className="px-5 py-3 text-[#374151] capitalize">{a.item_type === "path" ? "Ruta" : "Curso"}</td>
                    <td className="px-5 py-3 text-[#374151]">
                      {a.due_date ? (
                        <span>
                          {new Date(a.due_date).toLocaleDateString("es-MX")}
                          {dueState && <span className={`ml-2 text-xs ${dueState.color}`}>· {dueState.label}</span>}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {a.status === "completed" ? (
                        <span className="text-[#1B2E7A] text-xs font-medium">Completado</span>
                      ) : (
                        <span className="text-[#DC2626] text-xs font-medium">Pendiente</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {a.status === "pending" && (
                        <button
                          onClick={() => remind(a.id)}
                          className="text-[#1B2E7A] text-xs hover:underline mr-3"
                          data-testid={`remind-${a.id}`}
                        >
                          Recordar
                        </button>
                      )}
                      <button onClick={() => remove(a.id)} className="text-[#DC2626] text-xs hover:underline">Eliminar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CertificatesTab() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/certificates");
        setCerts(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-[#374151]">Cargando...</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Certificados emitidos · {certs.length}</p>
      {certs.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-8 text-center text-[#737373]">
          Aún no se han emitido certificados.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FFFFFF] text-[#737373]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Curso / Programa</th>
                <th className="text-left px-5 py-3 font-medium">Tipo</th>
                <th className="text-left px-5 py-3 font-medium">Calificación</th>
                <th className="text-left px-5 py-3 font-medium">Emitido</th>
                <th className="text-right px-5 py-3 font-medium">Acción</th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => (
                <tr key={c.id} className="border-t border-[#E5E7EB]">
                  <td className="px-5 py-3 text-[#1A1A1A] font-medium">{c.course_title}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-md ${c.certificate_type === "master" ? "bg-[#EAB308]/15 text-[#8C5E1A]" : "bg-[#1B2E7A]/10 text-[#1B2E7A]"}`}>
                      {c.certificate_type === "master" ? "Maestro" : "Curso"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[#374151]">{c.score != null ? `${c.score}%` : "—"}</td>
                  <td className="px-5 py-3 text-[#374151]">
                    {new Date(c.issued_at).toLocaleDateString("es-MX")}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <a
                      href={`${API}/certificates/${c.id}/download`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#1B2E7A] text-xs hover:underline"
                      data-testid={`download-cert-${c.id}`}
                    >
                      Ver PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InsightsTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/insights");
      setReport(r.data);
    } catch {
      toast.error("Error al generar análisis");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="bg-gradient-to-br from-[#F3F4F6] to-[#FFFFFF] border border-[#E5E7EB] rounded-md p-6 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" /> Análisis con IA
            </p>
            <h3 className="font-heading text-2xl text-[#1A1A1A]">Diagnóstico inteligente del programa</h3>
            <p className="text-sm text-[#374151] mt-2 max-w-2xl">
              Genera un análisis automatizado del desempeño de tu equipo: fortalezas, áreas de oportunidad
              y acciones recomendadas. Usa IA para detectar patrones que podrías pasar por alto.
            </p>
          </div>
          <Button
            onClick={generate}
            disabled={loading}
            className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
            data-testid="generate-insights-button"
          >
            {loading ? "Analizando..." : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generar análisis
              </>
            )}
          </Button>
        </div>
      </div>

      {report && (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-6">
          <p className="text-xs text-[#6B7280] mb-3">
            Generado el {new Date(report.generated_at).toLocaleString("es-MX")}
          </p>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[#1A1A1A] leading-relaxed">
            {report.report_markdown}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminAssignments() {
  const [courses, setCourses] = useState([]);
  const [paths, setPaths] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const refreshAll = async () => {
    const [c, p, u, a] = await Promise.all([
      api.get("/courses"),
      api.get("/admin/paths"),
      api.get("/admin/users"),
      api.get("/admin/assignments"),
    ]);
    setCourses(c.data);
    setPaths(p.data);
    setUsers(u.data.filter((x) => x.role !== "admin"));
    setAssignments(a.data);
  };

  const refreshPaths = async () => {
    const r = await api.get("/admin/paths");
    setPaths(r.data);
  };

  const refreshAssignments = async () => {
    const r = await api.get("/admin/assignments");
    setAssignments(r.data);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  return (
    <div data-testid="admin-assignments-page">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">Solo administradores</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          Asignaciones e Inteligencia.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          Crea rutas formativas, asigna capacitación obligatoria con fechas límite, gestiona certificados
          y obtén análisis con IA para tomar mejores decisiones.
        </p>
      </div>

      <Tabs defaultValue="paths">
        <TabsList className="bg-[#F3F4F6] border border-[#E5E7EB]">
          <TabsTrigger value="paths" className="data-[state=active]:bg-white" data-testid="tab-paths">
            <Route className="h-4 w-4 mr-2" /> Rutas
          </TabsTrigger>
          <TabsTrigger value="assignments" className="data-[state=active]:bg-white" data-testid="tab-assignments">
            <ListChecks className="h-4 w-4 mr-2" /> Asignaciones
          </TabsTrigger>
          <TabsTrigger value="certificates" className="data-[state=active]:bg-white" data-testid="tab-certificates">
            <Award className="h-4 w-4 mr-2" /> Certificados
          </TabsTrigger>
          <TabsTrigger value="insights" className="data-[state=active]:bg-white" data-testid="tab-insights">
            <Sparkles className="h-4 w-4 mr-2" /> Insights IA
          </TabsTrigger>
        </TabsList>

        <TabsContent value="paths" className="mt-6">
          <PathsTab courses={courses} paths={paths} refreshPaths={refreshPaths} />
        </TabsContent>
        <TabsContent value="assignments" className="mt-6">
          <AssignmentsTab
            courses={courses}
            paths={paths}
            users={users}
            assignments={assignments}
            refreshAssignments={refreshAssignments}
          />
        </TabsContent>
        <TabsContent value="certificates" className="mt-6">
          <CertificatesTab />
        </TabsContent>
        <TabsContent value="insights" className="mt-6">
          <InsightsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
